import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Api as ApiIcon,
  BarChart as BarChartIcon,
  HelpOutline as HelpOutlineIcon,
  Folder as FolderIcon,
  InfoOutlined as InfoOutlinedIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  apiService,
  Collection,
  DashboardSummary,
  Request,
  Project,
  Environment,
  ExecutionHistorySummary,
} from '../services/apiService';
import { getTestCycles } from '../services/testCycleService';
import { useProjects } from '../context/ProjectContext';
import { useCollectionExecution } from '../context/CollectionExecutionContext';
import { PROJECT_SELECT_CONTROL_WIDTH } from './Projects';
import { useAuth } from '../context/AuthContext';
import { DFT_BRAND } from '../styles/brandPalette';
import { getHelpPathForPage } from '../utils/helpPageMapping';
import { mergeRequestAndDataSetAssertions } from '../utils/assertionsMerge';
import { parseAssertionRulesJson, type AssertionRule } from '../types/assertionRules';
import type { RequestAssertion } from '../types/assertions';
import type { RequestDataSet } from '../types/dataSets';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

/** Card inner rhythm — same 14px type as Collections workspace cards. */
const SPACING = {
  section: 1.25,
  card: 1.5,
  header: 1.25,
} as const;

/** Chart height by CSS viewport (OS scale already in these widths). */
const CHART_BOX_HEIGHT = { xs: 180, sm: 200, md: 220, lg: 240, xl: 280 } as const;

/** Match Collections page chrome: canvas + inner 14px cards. */
const DASHBOARD_PAGE_BG = DFT_BRAND.bg;
const DASHBOARD_PAGE_GRADIENT = 'none';
const DASHBOARD_CARD_RADIUS = '14px';
const dashboardPageSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  px: 0,
  py: 0,
  bgcolor: 'transparent',
  background: DASHBOARD_PAGE_GRADIENT,
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  boxSizing: 'border-box' as const,
  overflowX: 'hidden',
  minHeight: {
    xs: 'calc(100dvh - 64px)',
    md: '100%',
  },
  '& .MuiCard-root': {
    borderRadius: `${DASHBOARD_CARD_RADIUS} !important`,
    overflow: 'hidden',
  },
  '& .MuiAlert-root': {
    borderRadius: '12px',
  },
  '& .MuiTypography-body2': {
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
  '& .MuiTypography-caption': {
    fontSize: '0.75rem',
    lineHeight: 1.4,
  },
} as const;
const DASHBOARD_WORKSPACE_SX = {
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '0 12px 12px 12px',
  border: `1px solid ${DFT_BRAND.line}`,
  borderTop: `1px solid ${DFT_BRAND.line}`,
  bgcolor: DFT_BRAND.bg,
  boxShadow: 'none',
  overflow: 'visible',
  p: { xs: 1.25, sm: 1.5 },
} as const;
const dashboardCardBorderSx = {
  position: 'relative' as const,
  border: `1px solid ${DFT_BRAND.line}`,
  borderRadius: `${DASHBOARD_CARD_RADIUS} !important`,
  boxShadow: '0 1px 2px rgba(23, 36, 65, 0.04)',
  bgcolor: DFT_BRAND.paper,
  overflow: 'hidden',
} as const;

const metricCardSx = {
  ...dashboardCardBorderSx,
  '&::before': {
    content: '""',
    position: 'absolute',
    height: 3,
    left: 0,
    right: 0,
    top: 0,
    background: DFT_BRAND.metricAccent,
    borderRadius: `${DASHBOARD_CARD_RADIUS} ${DASHBOARD_CARD_RADIUS} 0 0`,
  },
} as const;

const metricIconSx = {
  position: 'absolute' as const,
  right: 12,
  top: 14,
  color: DFT_BRAND.icon,
  background: DFT_BRAND.tint,
  borderRadius: '12px',
  p: 0.75,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

const dashboardLinkSx = {
  mt: 'auto',
  alignSelf: 'flex-start',
  color: DFT_BRAND.link,
  fontWeight: 500,
  fontSize: '0.8125rem !important',
  px: 0,
  minHeight: 0,
  textTransform: 'none' as const,
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
} as const;

/**
 * Request-scoped assertions plus data-set–scoped assertions for that request.
 * Matches AssertionManager / backend: data-set rows use data_set_id only (request_id null),
 * so getAssertionsByRequest alone undercounts (e.g. Suggested Assertions added on a data set row).
 */
async function fetchMergedAssertionsForRequest(
  requestId: number,
  dataSets?: RequestDataSet[],
): Promise<RequestAssertion[]> {
  const [resolvedDataSets, byRequest] = await Promise.all([
    dataSets != null ? Promise.resolve(dataSets) : apiService.getDataSetsByRequest(requestId),
    apiService.getAssertionsByRequest(requestId),
  ]);
  const byDataSetLists = await Promise.all(
    resolvedDataSets.map((ds) =>
      ds.id != null
        ? apiService.getAssertionsByDataSet(ds.id).catch(() => [] as RequestAssertion[])
        : Promise.resolve([] as RequestAssertion[]),
    ),
  );
  return mergeRequestAndDataSetAssertions(byRequest, byDataSetLists.flat());
}

function assertionRuleParentKey(parentRuleId?: string | null): string {
  const id = parentRuleId?.trim();
  return id ? id : 'null';
}

/** Enabled Assertion Rule Builder rows shown in the assertions table (not request_assertion rows). */
function countEnabledAssertionRules(rulesJson: string | null | undefined): number {
  const rules = parseAssertionRulesJson(rulesJson).filter((rule) => rule.enabled !== false);
  const byParent = new Map<string, AssertionRule[]>();
  for (const rule of rules) {
    const key = assertionRuleParentKey(rule.parentRuleId);
    const list = byParent.get(key);
    if (list) list.push(rule);
    else byParent.set(key, [rule]);
  }
  const seen = new Set<AssertionRule>();
  const walk = (nodes: AssertionRule[] | undefined): number => {
    if (!nodes?.length) return 0;
    let count = 0;
    for (const rule of nodes) {
      if (seen.has(rule)) continue;
      seen.add(rule);
      count += 1;
      const id = rule.id?.trim();
      if (id) count += walk(byParent.get(id));
    }
    return count;
  };
  return walk(byParent.get('null'));
}

const TEAM_ENGAGEMENT_SERIES_META: {
  key: string;
  label: string;
  color: string;
}[] = [
  { key: 'active', label: 'Active users', color: DFT_BRAND.chartBlue },
  { key: 'api', label: 'API users', color: DFT_BRAND.chartTeal },
  { key: 'new', label: 'New API users', color: DFT_BRAND.chartOrange },
  { key: 'success', label: 'Successful API users', color: DFT_BRAND.chartGreen },
  { key: 'error', label: 'Error-only API users', color: DFT_BRAND.chartGold },
];

type ApiStatusKey = '2XX' | '3XX' | '4XX' | '5XX';

const API_RESPONSE_COLORS: Record<ApiStatusKey, string> = {
  '2XX': '#22c55e',
  '3XX': '#0ea5e9',
  '4XX': '#facc15',
  '5XX': '#ef4444',
};

const API_RESPONSE_KEYS: ApiStatusKey[] = ['2XX', '3XX', '4XX', '5XX'];

type AssetSeriesKey = 'requests' | 'collections' | 'datasets' | 'assertions' | 'testCycles';

const TOTAL_ASSETS_SERIES: {
  key: AssetSeriesKey;
  label: string;
  color: string;
}[] = [
  { key: 'requests', label: 'Total requests', color: DFT_BRAND.chartBlue },
  { key: 'collections', label: 'Total collections', color: DFT_BRAND.chartTeal },
  { key: 'datasets', label: 'Total datasets', color: DFT_BRAND.chartOrange },
  { key: 'assertions', label: 'Total assertions', color: DFT_BRAND.chartGold },
  { key: 'testCycles', label: 'Total test plans', color: DFT_BRAND.chartGreen },
];

type TestCycleAssetKey = 'requests' | 'datasets' | 'assertions';

type DashboardRecentRequest = Request & {
  dashboardLastRunAt?: string;
};

const TEST_CYCLE_ASSET_SERIES: {
  key: TestCycleAssetKey;
  label: string;
  color: string;
}[] = [
  { key: 'requests', label: 'Requests', color: DFT_BRAND.chartBlue },
  { key: 'datasets', label: 'Data sets', color: DFT_BRAND.chartOrange },
  { key: 'assertions', label: 'Assertions', color: DFT_BRAND.chartGold },
];

const buildLastSixMonthBuckets = () => {
  const now = new Date();
  return Array.from({ length: 6 }, (_, index) => {
    const monthStart = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() - (4 - index), 1);
    return {
      label: monthStart.toLocaleString(undefined, { month: 'short' }),
      monthStart,
      monthEnd,
    };
  });
};

const parseDateSafe = (value?: string | null): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getAxisTicks = (maxValue: number, steps: number): number[] => {
  if (!Number.isFinite(maxValue) || maxValue <= 0) {
    return [0];
  }
  const stepSize = maxValue / steps;
  const ticks: number[] = [];
  for (let i = steps; i >= 0; i -= 1) {
    const value = +(stepSize * i).toFixed(1);
    ticks.push(value);
  }
  return ticks;
};

/** Last assertion total per project, so returning to the dashboard does not paint 0 first. */
const dashboardAssertionCountByProject = new Map<number, number>();

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedProject, projects, setSelectedProject, isLoading: projectsLoading }: {
    selectedProject: Project | null;
    projects: Project[];
    setSelectedProject: (project: Project | null) => void;
    isLoading: boolean;
  } = useProjects();
  const { activeExecution } = useCollectionExecution();
  const collectionRunInProgressRef = useRef(false);
  collectionRunInProgressRef.current = activeExecution?.status === 'running';
  const pendingDashboardRefreshRef = useRef(false);
  const refreshDashboardRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (user?.role === 'ADMIN') {
      navigate('/manage-account?tab=dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Restore last selected project for the Dashboard when projects are loaded
  useEffect(() => {
    if (projectsLoading) return;
    if (selectedProject) return;
    if (!projects || projects.length === 0) return;

    const storedId = window.localStorage.getItem('dashboard:lastProjectId');
    const storedProject = storedId ? projects.find((p) => p.id === Number(storedId)) : null;

    if (storedProject) {
      setSelectedProject(storedProject);
    }
  }, [projectsLoading, projects, selectedProject, setSelectedProject]);

  // Persist last selected project so it becomes the default next time
  useEffect(() => {
    if (selectedProject?.id != null) {
      window.localStorage.setItem('dashboard:lastProjectId', String(selectedProject.id));
    }
  }, [selectedProject]);

  const [collections, setCollections] = useState<Collection[]>([]);
  const [allProjectCollections, setAllProjectCollections] = useState<Collection[]>([]);
  const [recentRequests, setRecentRequests] = useState<DashboardRecentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalRequests, setTotalRequests] = useState(0);
  const [totalCollections, setTotalCollections] = useState(0);
  const [totalSubCollections, setTotalSubCollections] = useState(0);
  const [totalDataSets, setTotalDataSets] = useState(0);
  const [totalAssertions, setTotalAssertions] = useState<number | null>(() => {
    const projectId = selectedProject?.id;
    if (projectId == null || !dashboardAssertionCountByProject.has(projectId)) return null;
    return dashboardAssertionCountByProject.get(projectId) ?? null;
  });
  const [totalTestCycles, setTotalTestCycles] = useState(0);
  const [totalOrganizationMembers, setTotalOrganizationMembers] = useState(0);
  const [collectionRequestCounts, setCollectionRequestCounts] = useState<Record<number, number>>({});
  const [apiResponseViewMode, setApiResponseViewMode] = useState<'total' | 'percentage'>('total');
  const [testCycleAssetViewMode, setTestCycleAssetViewMode] = useState<'total' | 'percentage'>('total');
  const [userDistributionViewMode, setUserDistributionViewMode] = useState<'total' | 'percentage'>('total');
  const [activeWorkspacesViewMode, setActiveWorkspacesViewMode] = useState<'total' | 'percentage'>('total');
  const [workspaceElementsViewMode, setWorkspaceElementsViewMode] = useState<'total' | 'percentage'>('total');
  const [activeEngagementSeriesKeys, setActiveEngagementSeriesKeys] = useState<Record<string, boolean>>(
    () =>
      TEAM_ENGAGEMENT_SERIES_META.reduce<Record<string, boolean>>((acc, series) => {
        acc[series.key] = true;
        return acc;
      }, {}),
  );
  const [activeAssetSeriesKeys, setActiveAssetSeriesKeys] = useState<Record<AssetSeriesKey, boolean>>({
    requests: true,
    collections: true,
    datasets: true,
    assertions: true,
    testCycles: true,
  });
  const [assetSeriesTooltip, setAssetSeriesTooltip] = useState<{
    label: string;
    values: Record<AssetSeriesKey, number>;
    x: number;
    y: number;
  } | null>(null);
  const [testCycles, setTestCycles] = useState<any[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [allProjectRequests, setAllProjectRequests] = useState<Request[]>([]);
  const [executionHistoryRows, setExecutionHistoryRows] = useState<ExecutionHistorySummary[]>([]);
  const [projectDataSetDates, setProjectDataSetDates] = useState<Date[]>([]);
  const [projectAssertionDates, setProjectAssertionDates] = useState<Date[]>([]);
  const [selectedTestCycleId, setSelectedTestCycleId] = useState<'all' | number>('all');
  const [collectionLastRunAtById, setCollectionLastRunAtById] = useState<Record<number, string>>({});
  const [requestLastRunAtById, setRequestLastRunAtById] = useState<Record<number, string>>({});
  const [isCollectionsDialogOpen, setIsCollectionsDialogOpen] = useState(false);
  const [isRequestsDialogOpen, setIsRequestsDialogOpen] = useState(false);
  const [isTestCyclesDialogOpen, setIsTestCyclesDialogOpen] = useState(false);
  const projectSelectRef = useRef<HTMLDivElement | null>(null);
  const [projectMenuWidth, setProjectMenuWidth] = useState<number>(0);
  const dashboardSummaryMetricsRef = useRef(false);

  useEffect(() => {
    if (!selectedProject) {
      setLoading(false);
      setTotalCollections(0);
      setTotalSubCollections(0);
      setTotalRequests(0);
      setTotalDataSets(0);
      setTotalTestCycles(0);
      setTotalOrganizationMembers(0);
      setCollections([]);
      setAllProjectCollections([]);
      setRecentRequests([]);
      setCollectionRequestCounts({});
      setEnvironments([]);
      setAllProjectRequests([]);
      setExecutionHistoryRows([]);
      setProjectDataSetDates([]);
      setProjectAssertionDates([]);
      setCollectionLastRunAtById({});
      setRequestLastRunAtById({});
      return;
    }

    let refreshDebounce: number | null = null;
    let isCancelled = false;

    const refresh = async () => {
      let loadingWatchdog: number | null = null;
      let renderedFromSummary = false;
      dashboardSummaryMetricsRef.current = false;
      try {
        if (isCancelled) return;
        setLoading(true);
        setError(null);
        // Prevent an infinite spinner if any dashboard API call hangs.
        loadingWatchdog = window.setTimeout(() => {
          if (isCancelled) return;
          setLoading(false);
          setError((prev) => prev ?? 'Dashboard is taking longer than expected. Please refresh and try again.');
        }, 20000);

        // Fast path: get lightweight counts/charts summary first so dashboard paints quickly.
        try {
          const summary: DashboardSummary = await apiService.getDashboardSummary(selectedProject.id);
          if (isCancelled) return;
          setTotalCollections(summary.totalCollections - summary.totalSubCollections);
          setTotalSubCollections(summary.totalSubCollections);
          setTotalRequests(summary.totalRequests);
          setTotalDataSets(summary.totalDataSets);
          dashboardAssertionCountByProject.set(selectedProject.id, summary.totalAssertions);
          setTotalAssertions(summary.totalAssertions);
          setTotalTestCycles(summary.totalTestCycles);
          setTotalOrganizationMembers(
            typeof summary.totalOrganizationMembers === 'number' ? summary.totalOrganizationMembers : 0,
          );
          dashboardSummaryMetricsRef.current = true;
          renderedFromSummary = true;
          setLoading(false);
        } catch {
          if (!isCancelled) setTotalOrganizationMembers(0);
          // Keep full refresh path even if summary endpoint is unavailable.
        }

        const collectionsData = await apiService.getCollectionsByProject(selectedProject.id);
        if (isCancelled) return;
        setAllProjectCollections(collectionsData);
        const rootCollectionsCount = collectionsData.filter((c) => !c.parentCollectionId).length;
        const subCollectionsCount = collectionsData.filter((c) => !!c.parentCollectionId).length;
        setTotalCollections(rootCollectionsCount);
        setTotalSubCollections(subCollectionsCount);
        try {
          const envs = await apiService.getEnvironmentsByProject(selectedProject.id);
          if (!isCancelled) setEnvironments(Array.isArray(envs) ? envs : []);
        } catch {
          if (!isCancelled) setEnvironments([]);
        }

        let historyRows: ExecutionHistorySummary[] = [];
        try {
          const historyPage = await apiService.getExecutionHistoryPage(selectedProject.id, 0, 200);
          historyRows = historyPage?.content ?? [];
        } catch (histErr) {
          console.warn('Dashboard: could not load execution history for recents', histErr);
        }

        const collectionLastRunMs = new Map<number, number>();
        const requestLastRunAt = new Map<number, string>();
        for (const row of historyRows) {
          const rowMs = new Date(row.createdAt).getTime();
          if (row.collectionId != null) {
            const prev = collectionLastRunMs.get(row.collectionId) ?? 0;
            if (rowMs > prev) collectionLastRunMs.set(row.collectionId, rowMs);
          }
          if (row.requestId != null) {
            const prevIso = requestLastRunAt.get(row.requestId);
            const prevMs = prevIso ? new Date(prevIso).getTime() : 0;
            if (rowMs > prevMs) requestLastRunAt.set(row.requestId, row.createdAt);
          }
        }
        const collectionLastRunAtObject: Record<number, string> = {};
        collectionLastRunMs.forEach((ms, collectionId) => {
          collectionLastRunAtObject[collectionId] = new Date(ms).toISOString();
        });
        const requestLastRunAtObject: Record<number, string> = {};
        requestLastRunAt.forEach((value, requestId) => {
          requestLastRunAtObject[requestId] = value;
        });

        // Recent collections: by last execution in project, then by last edit
        const recent = [...collectionsData]
          .sort((a, b) => {
            const runA = collectionLastRunMs.get(a.id) ?? 0;
            const runB = collectionLastRunMs.get(b.id) ?? 0;
            if (runA !== runB) return runB - runA;
            const aTs = a.updatedAt || a.createdAt || '';
            const bTs = b.updatedAt || b.createdAt || '';
            if (aTs && bTs) return new Date(bTs).getTime() - new Date(aTs).getTime();
            return (b.id || 0) - (a.id || 0);
          })
          .slice(0, 5);
        setCollections(recent);

        const allRequests: Request[] = await apiService.getRequestsByProject(selectedProject.id);
        if (isCancelled) return;
        const requestCounts: Record<number, number> = {};

        // Fetch requests per collection in parallel batches (sequential loop was N round-trips and felt very slow).
        const REQUEST_FETCH_CONCURRENCY = 8;
        for (let i = 0; i < collectionsData.length; i += REQUEST_FETCH_CONCURRENCY) {
          if (isCancelled) return;
          const batch = collectionsData.slice(i, i + REQUEST_FETCH_CONCURRENCY);
          const batchOut = await Promise.all(
            batch.map(async (collection) => {
              try {
                const requestsData = await apiService.getRequestsByCollection(collection.id);
                return { collectionId: collection.id, requestsData };
              } catch (err) {
                console.warn(`Failed to fetch requests for collection ${collection.id}:`, err);
                return { collectionId: collection.id, requestsData: [] as any[] };
              }
            }),
          );
          for (const { collectionId, requestsData } of batchOut) {
            // allRequests already comes from getRequestsByProject above; do not push per-collection
            // rows here or every request is duplicated (API calls count and Recent requests list).
            requestCounts[collectionId] = requestsData.length;
          }
        }

        // Recent requests: by last execution in project, then by last edit (does not need per-request assertion fetches).
        const sortedRequests = [...allRequests].sort((a, b) => {
          const runA = requestLastRunAt.has(a.id)
            ? new Date(requestLastRunAt.get(a.id)!).getTime()
            : 0;
          const runB = requestLastRunAt.has(b.id)
            ? new Date(requestLastRunAt.get(b.id)!).getTime()
            : 0;
          if (runA !== runB) return runB - runA;
          const aTs = a.updatedAt || a.createdAt || '';
          const bTs = b.updatedAt || b.createdAt || '';
          if (aTs && bTs) return new Date(bTs).getTime() - new Date(aTs).getTime();
          return (b.id || 0) - (a.id || 0);
        });
        const topRecent = sortedRequests.slice(0, 5).map((req) => ({
          ...req,
          dashboardLastRunAt: requestLastRunAt.get(req.id),
        }));

        if (isCancelled) return;
        setAllProjectRequests(allRequests);
        setExecutionHistoryRows(historyRows);
        setCollectionLastRunAtById(collectionLastRunAtObject);
        setRequestLastRunAtById(requestLastRunAtObject);
        setTotalRequests(allRequests.length);
        setCollectionRequestCounts(requestCounts);
        setCollections(recent);
        setRecentRequests(topRecent);
        // Chart series for datasets/assertions are filled after; keep summary counts for stat cards.
        setProjectDataSetDates([]);
        setProjectAssertionDates([]);
        setLoading(false);

      } catch (error: any) {
        if (isCancelled) return;
        console.error('Error fetching dashboard data:', error);
        if (!renderedFromSummary) {
          setError(error.response?.data?.message || 'Failed to fetch dashboard data');
        }
      } finally {
        if (loadingWatchdog != null) {
          window.clearTimeout(loadingWatchdog);
        }
        if (isCancelled) return;
        setLoading(false);
      }
    };

    // Initial load
    refresh();
    refreshDashboardRef.current = () => {
      void refresh();
    };

    // Real-time-ish updates: request execution/config changes already dispatch these events.
    // We debounce to avoid hammering the backend on rapid successive executions.
    // Collection request-by-request and round-robin emit one event per request; defer until the run ends.
    const scheduleRefresh = () => {
      if (collectionRunInProgressRef.current) {
        pendingDashboardRefreshRef.current = true;
        return;
      }
      if (refreshDebounce != null) {
        window.clearTimeout(refreshDebounce);
      }
      refreshDebounce = window.setTimeout(() => {
        refresh();
      }, 750);
    };

    window.addEventListener('global-history-updated', scheduleRefresh);
    window.addEventListener('request-config-history-updated', scheduleRefresh);
    window.addEventListener('dashboard-recent-updated', scheduleRefresh);

    return () => {
      isCancelled = true;
      if (refreshDebounce != null) window.clearTimeout(refreshDebounce);
      window.removeEventListener('global-history-updated', scheduleRefresh);
      window.removeEventListener('request-config-history-updated', scheduleRefresh);
      window.removeEventListener('dashboard-recent-updated', scheduleRefresh);
    };
  }, [selectedProject]);

  useEffect(() => {
    if (activeExecution?.status === 'running') return;
    if (!pendingDashboardRefreshRef.current) return;
    pendingDashboardRefreshRef.current = false;
    refreshDashboardRef.current?.();
  }, [activeExecution?.status]);

  useEffect(() => {
    if (!selectedProject || allProjectRequests.length === 0) {
      setProjectDataSetDates([]);
      setProjectAssertionDates([]);
      return;
    }

    let isCancelled = false;

    const refreshRequestLevelMetrics = async () => {
      try {
        const dataSetDates: Date[] = [];
        const assertionDates: Date[] = [];
        let dataSetCount = 0;
        let assertionCount = 0;
        const requestCreatedAtMap = new Map<number, string>();
        allProjectRequests.forEach((req) => {
          requestCreatedAtMap.set(req.id, req.createdAt);
        });

        const ASSERTION_FETCH_CONCURRENCY = 8;
        for (let i = 0; i < allProjectRequests.length; i += ASSERTION_FETCH_CONCURRENCY) {
          if (isCancelled) return;
          const reqBatch = allProjectRequests.slice(i, i + ASSERTION_FETCH_CONCURRENCY);
          const out = await Promise.all(
            reqBatch.map(async (req) => {
              try {
                const dataSets = await apiService.getDataSetsByRequest(req.id);
                const assertions = await fetchMergedAssertionsForRequest(req.id, dataSets);
                return {
                  reqId: req.id,
                  dataSets,
                  assertions,
                  ruleCount: countEnabledAssertionRules(req.assertionRulesJson),
                };
              } catch {
                return { reqId: req.id, dataSets: [], assertions: [], ruleCount: 0 };
              }
            }),
          );

          for (const { reqId, dataSets, assertions, ruleCount } of out) {
            const fallbackDate = parseDateSafe(requestCreatedAtMap.get(reqId));
            dataSetCount += dataSets.length;
            assertionCount += assertions.length + ruleCount;
            for (const ds of dataSets) {
              const dsDate = parseDateSafe(ds?.createdAt) ?? fallbackDate;
              if (dsDate) dataSetDates.push(dsDate);
            }
            for (const assertion of assertions) {
              const assertionDate = parseDateSafe(assertion?.createdAt) ?? fallbackDate;
              if (assertionDate) assertionDates.push(assertionDate);
            }
            if (fallbackDate) {
              for (let n = 0; n < ruleCount; n += 1) {
                assertionDates.push(fallbackDate);
              }
            }
          }
        }

        if (isCancelled) return;
        setProjectDataSetDates(dataSetDates);
        setProjectAssertionDates(assertionDates);
        if (!dashboardSummaryMetricsRef.current) {
          setTotalDataSets(dataSetCount);
          if (selectedProject?.id != null) {
            dashboardAssertionCountByProject.set(selectedProject.id, assertionCount);
          }
          setTotalAssertions(assertionCount);
        }
      } catch {
        // Keep existing metrics on background refresh errors.
      }
    };

    void refreshRequestLevelMetrics();

    return () => {
      isCancelled = true;
    };
  }, [selectedProject, allProjectRequests]);

  useEffect(() => {
    const loadTestCycles = async () => {
      if (!selectedProject) {
        setTestCycles([]);
        setSelectedTestCycleId('all');
        setTotalTestCycles(0);
        return;
      }
      try {
        const cycles = await getTestCycles(selectedProject.id);
        const arr = Array.isArray(cycles) ? cycles : [];
        setTestCycles(arr);
        setTotalTestCycles(arr.length);
        setSelectedTestCycleId('all');
      } catch (err) {
        // Keep summary-derived count; only clear list state on failure.
        setTestCycles([]);
        setSelectedTestCycleId('all');
      }
    };
    loadTestCycles();

    const onTestPlanUpdated = () => {
      if (collectionRunInProgressRef.current) {
        pendingDashboardRefreshRef.current = true;
        return;
      }
      void loadTestCycles();
    };
    window.addEventListener('dashboard-recent-updated', onTestPlanUpdated);
    return () => {
      window.removeEventListener('dashboard-recent-updated', onTestPlanUpdated);
    };
  }, [selectedProject]);

  const monthBuckets = useMemo(() => buildLastSixMonthBuckets(), []);
  const teamEngagementMonths = useMemo(() => monthBuckets.map((b) => b.label), [monthBuckets]);

  const totalAssetsData = useMemo(() => {
    return monthBuckets.map((bucket) => {
      const inBucket = (d: Date | null) => d && d >= bucket.monthStart && d < bucket.monthEnd;
      return {
        label: bucket.label,
        values: {
          requests: allProjectRequests.filter((r) => inBucket(parseDateSafe(r.createdAt))).length,
          collections: allProjectCollections.filter((c) => inBucket(parseDateSafe(c.createdAt))).length,
          datasets: projectDataSetDates.filter(inBucket).length,
          assertions: projectAssertionDates.filter(inBucket).length,
          testCycles: testCycles.filter((tc: any) => inBucket(parseDateSafe(tc?.createdAt))).length,
        } as Record<AssetSeriesKey, number>,
      };
    });
  }, [monthBuckets, allProjectRequests, allProjectCollections, projectDataSetDates, projectAssertionDates, testCycles]);

  const testCycleAssetData = useMemo(
    () =>
      totalAssetsData.map((bucket) => ({
        month: bucket.label,
        requests: bucket.values.requests,
        datasets: bucket.values.datasets,
        assertions: bucket.values.assertions,
      })),
    [totalAssetsData],
  );

  const apiResponseData = useMemo(
    () =>
      monthBuckets.map((bucket) => {
        const row = { month: bucket.label, '2XX': 0, '3XX': 0, '4XX': 0, '5XX': 0 };
        executionHistoryRows.forEach((entry) => {
          const created = parseDateSafe(entry.createdAt);
          if (!created || created < bucket.monthStart || created >= bucket.monthEnd) return;
          const code = entry.statusCode ?? 0;
          if (code >= 200 && code < 300) row['2XX'] += 1;
          else if (code >= 300 && code < 400) row['3XX'] += 1;
          else if (code >= 400 && code < 500) row['4XX'] += 1;
          else if (code >= 500) row['5XX'] += 1;
        });
        return row;
      }),
    [monthBuckets, executionHistoryRows],
  );

  const teamEngagementSeries = useMemo(() => {
    const totalUsers = user ? 1 : 0;
    return TEAM_ENGAGEMENT_SERIES_META.map((series) => {
      if (series.key === 'active') {
        const values = monthBuckets.map((bucket) => {
          const hasActivity = executionHistoryRows.some((row) => {
            const d = parseDateSafe(row.createdAt);
            return d != null && d >= bucket.monthStart && d < bucket.monthEnd;
          });
          return hasActivity && totalUsers > 0 ? totalUsers : 0;
        });
        return { ...series, values };
      }
      if (series.key === 'api') {
        const values = monthBuckets.map((bucket) => {
          const hasActivity = executionHistoryRows.some((row) => {
            const d = parseDateSafe(row.createdAt);
            return d != null && d >= bucket.monthStart && d < bucket.monthEnd;
          });
          return hasActivity && totalUsers > 0 ? totalUsers : 0;
        });
        return { ...series, values };
      }
      if (series.key === 'new') {
        const firstActivity = executionHistoryRows
          .map((row) => parseDateSafe(row.createdAt))
          .filter((d): d is Date => d != null)
          .sort((a, b) => a.getTime() - b.getTime())[0];
        const values = monthBuckets.map((bucket) =>
          firstActivity && firstActivity >= bucket.monthStart && firstActivity < bucket.monthEnd ? totalUsers : 0,
        );
        return { ...series, values };
      }
      if (series.key === 'success') {
        const values = monthBuckets.map((bucket) => {
          const hasSuccess = executionHistoryRows.some((row) => {
            const d = parseDateSafe(row.createdAt);
            return d != null && d >= bucket.monthStart && d < bucket.monthEnd && row.statusCode >= 200 && row.statusCode < 400;
          });
          return hasSuccess && totalUsers > 0 ? totalUsers : 0;
        });
        return { ...series, values };
      }
      const values = monthBuckets.map((bucket) => {
        const hasErrorOnly = executionHistoryRows.some((row) => {
          const d = parseDateSafe(row.createdAt);
          return d != null && d >= bucket.monthStart && d < bucket.monthEnd && row.statusCode >= 400;
        });
        return hasErrorOnly && totalUsers > 0 ? totalUsers : 0;
      });
      return { ...series, values };
    });
  }, [monthBuckets, executionHistoryRows, user]);

  const userDistributionChartData = useMemo(
    () =>
      monthBuckets.map((bucket) => {
        const hasActivity = executionHistoryRows.some((row) => {
          const d = parseDateSafe(row.createdAt);
          return d != null && d >= bucket.monthStart && d < bucket.monthEnd;
        });
        return { month: bucket.label, 'Active users': hasActivity && user ? 1 : 0 };
      }),
    [monthBuckets, executionHistoryRows, user],
  );

  const activeWorkspacesChartData = useMemo(
    () =>
      monthBuckets.map((bucket) => {
        const projectCreated = parseDateSafe(selectedProject?.createdAt);
        const isActive = projectCreated != null && projectCreated < bucket.monthEnd;
        return {
          month: bucket.label,
          Internal: isActive ? 1 : 0,
          Partner: 0,
          Private: 0,
          Public: 0,
        };
      }),
    [monthBuckets, selectedProject],
  );

  const workspaceElementsChartData = useMemo(
    () =>
      monthBuckets.map((bucket) => {
        const inBucket = (d: Date | null) => d && d >= bucket.monthStart && d < bucket.monthEnd;
        return {
          month: bucket.label,
          Collections: allProjectCollections.filter((c) => inBucket(parseDateSafe(c.createdAt))).length,
          Environments: environments.filter((e) => inBucket(parseDateSafe(e.createdAt))).length,
          Mocks: 0,
          Monitors: 0,
        };
      }),
    [monthBuckets, allProjectCollections, environments],
  );

  const latest25Collections = useMemo(
    () =>
      [...allProjectCollections]
        .sort((a, b) => {
          const runA = collectionLastRunAtById[a.id] ? new Date(collectionLastRunAtById[a.id]).getTime() : 0;
          const runB = collectionLastRunAtById[b.id] ? new Date(collectionLastRunAtById[b.id]).getTime() : 0;
          if (runA !== runB) return runB - runA;
          const aTs = a.updatedAt || a.createdAt || '';
          const bTs = b.updatedAt || b.createdAt || '';
          if (aTs && bTs) return new Date(bTs).getTime() - new Date(aTs).getTime();
          return (b.id || 0) - (a.id || 0);
        })
        .slice(0, 25),
    [allProjectCollections, collectionLastRunAtById],
  );

  const latest25Requests = useMemo(
    () =>
      [...allProjectRequests]
        .sort((a, b) => {
          const runA = requestLastRunAtById[a.id] ? new Date(requestLastRunAtById[a.id]).getTime() : 0;
          const runB = requestLastRunAtById[b.id] ? new Date(requestLastRunAtById[b.id]).getTime() : 0;
          if (runA !== runB) return runB - runA;
          const aTs = a.updatedAt || a.createdAt || '';
          const bTs = b.updatedAt || b.createdAt || '';
          if (aTs && bTs) return new Date(bTs).getTime() - new Date(aTs).getTime();
          return (b.id || 0) - (a.id || 0);
        })
        .slice(0, 25)
        .map((req) => ({
          ...req,
          dashboardLastRunAt: requestLastRunAtById[req.id],
        })),
    [allProjectRequests, requestLastRunAtById],
  );

  const latest30TestCycles = useMemo(() => {
    const ts = (tc: { updatedAt?: string; createdAt?: string; id?: number }) => {
      const d = parseDateSafe(tc.updatedAt) ?? parseDateSafe(tc.createdAt);
      return d?.getTime() ?? 0;
    };
    return [...testCycles]
      .sort((a, b) => {
        const diff = ts(b) - ts(a);
        if (diff !== 0) return diff;
        return (b.id || 0) - (a.id || 0);
      })
      .slice(0, 30);
  }, [testCycles]);

  const openTestCyclesDialog = async () => {
    setIsTestCyclesDialogOpen(true);
    if (!selectedProject) return;
    try {
      const cycles = await getTestCycles(selectedProject.id);
      const arr = Array.isArray(cycles) ? cycles : [];
      setTestCycles(arr);
      setTotalTestCycles(arr.length);
    } catch {
      // keep existing list
    }
  };

  const getMethodColor = (method: string) => {
    const colors: { [key: string]: string } = {
      GET: '#61affe',
      POST: '#49cc90',
      PUT: '#fca130',
      DELETE: '#f93e3e',
      PATCH: '#50e3c2',
    };
    return colors[method.toUpperCase()] || '#666';
  };

  const formatShortDate = (timestamp?: string) => {
    if (!timestamp) return '—';
    const d = new Date(timestamp);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const syncProjectMenuPaperWidth = (paperEl: HTMLElement) => {
    const width = projectSelectRef.current?.getBoundingClientRect().width ?? 0;
    if (width > 0) {
      const w = `${Math.round(width)}px`;
      paperEl.style.width = w;
      paperEl.style.minWidth = w;
      paperEl.style.maxWidth = w;
      setProjectMenuWidth(Math.round(width));
    }
  };

  const projectSelect = (
    <FormControl
      ref={projectSelectRef}
      size="small"
      variant="outlined"
      sx={{
        width: PROJECT_SELECT_CONTROL_WIDTH,
        flexShrink: 0,
        '& .MuiOutlinedInput-root': {
          height: 36,
          minHeight: 36,
          backgroundColor: DFT_BRAND.paper,
          borderRadius: '10px',
        },
      }}
      aria-label="Select project"
    >
      <InputLabel id="dashboard-project-select-label">Project</InputLabel>
      <Select
        labelId="dashboard-project-select-label"
        id="dashboard-project-select"
        value={selectedProject ? String((selectedProject as Project).id) : ''}
        label="Project"
        onChange={(e) => {
          const projectId = parseInt(e.target.value, 10);
          if (!isNaN(projectId)) {
            const project = projects.find((p) => p.id === projectId);
            setSelectedProject(project || null);
          }
        }}
        onOpen={() => {
          const width = projectSelectRef.current?.getBoundingClientRect().width ?? 0;
          if (width > 0) setProjectMenuWidth(Math.round(width));
        }}
        sx={{
          width: '100%',
          bgcolor: DFT_BRAND.paper,
          '& .MuiSelect-select': {
            whiteSpace: 'nowrap',
          },
        }}
        MenuProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          TransitionProps: {
            onEntering: (node) => syncProjectMenuPaperWidth(node as HTMLElement),
          },
          PaperProps: {
            sx: {
              maxHeight: 40 * 8 + 16,
              mt: 0.5,
              boxSizing: 'border-box',
              ...(projectMenuWidth > 0
                ? {
                    width: `${projectMenuWidth}px !important`,
                    minWidth: `${projectMenuWidth}px !important`,
                    maxWidth: `${projectMenuWidth}px !important`,
                  }
                : {}),
            },
          },
          MenuListProps: {
            sx: {
              maxHeight: 40 * 8 + 16,
              overflowY: 'auto',
              py: 0.5,
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              '& .MuiMenuItem-root': {
                minHeight: 40,
                whiteSpace: 'nowrap',
              },
            },
          },
        }}
      >
        {projectsLoading ? (
          <MenuItem disabled value="">
            <CircularProgress size={20} /> Loading...
          </MenuItem>
        ) : (
          projects.map((project) => (
            <MenuItem key={project.id} value={project.id} sx={{ fontSize: '0.75rem', py: 1 }}>
              {project.name}
            </MenuItem>
          ))
        )}
        {projects.length === 0 && !projectsLoading && (
          <MenuItem disabled>No projects found</MenuItem>
        )}
      </Select>
    </FormControl>
  );

  const helpButton = (
    <Button
      size="small"
      variant="outlined"
      startIcon={<HelpOutlineIcon />}
      onClick={() => navigate(getHelpPathForPage('DASHBOARD'))}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.875rem',
        color: '#1d4ed8',
        borderColor: '#bfdbfe',
        bgcolor: '#ffffff',
        height: 36,
        minHeight: 36,
        borderRadius: 0,
        px: 1.1,
        '&:hover': {
          borderColor: '#93c5fd',
          bgcolor: '#eff6ff',
        },
      }}
      aria-label="Open help for Dashboard"
    >
      Help
    </Button>
  );

  const newCollectionButton = (
    <Button
      variant="contained"
      color="primary"
      size="small"
      startIcon={<AddIcon />}
      onClick={() => navigate('/collections?newCollection=1')}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        height: 36,
        minHeight: 36,
        borderRadius: 1.5,
        bgcolor: '#1d4ed8',
        '&:hover': { bgcolor: '#1e40af' },
        px: 1.2,
        fontSize: '0.875rem',
        boxShadow: 'none',
      }}
    >
      New Collection
    </Button>
  );

  const dashboardHeader = (
    <Box sx={{ width: '100%', flexShrink: 0, overflow: 'visible' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: 1,
          minWidth: 0,
          px: { xs: 1.25, sm: 1.5 },
          pt: 1.25,
          pb: 0.5,
          minHeight: 36,
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{
            m: 0,
            fontWeight: 700,
            height: 36,
            lineHeight: '36px',
            display: 'flex',
            alignItems: 'center',
            letterSpacing: '-0.02em',
            color: DFT_BRAND.text,
          }}
        >
          Dashboard
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            overflow: 'visible',
            minHeight: 36,
            '& .MuiButton-root': {
              display: 'inline-flex',
              alignItems: 'center',
              lineHeight: 1,
            },
            '& .MuiButton-startIcon': {
              display: 'inline-flex',
              alignItems: 'center',
              marginTop: 0,
              marginBottom: 0,
            },
          }}
        >
          {helpButton}
          {newCollectionButton}
          {projectSelect}
        </Box>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box component="main" sx={dashboardPageSx} role="status" aria-live="polite">
        {dashboardHeader}
        <Box
          sx={{
            ...DASHBOARD_WORKSPACE_SX,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 280,
          }}
        >
          <CircularProgress aria-label="Loading dashboard" />
        </Box>
      </Box>
    );
  }

  if (!selectedProject) {
    return (
        <Box
          component="main"
          sx={dashboardPageSx}
        >
          {dashboardHeader}
          <Box sx={DASHBOARD_WORKSPACE_SX}>
            <Alert severity="info" role="alert">
              Please select a project to view dashboard statistics.
            </Alert>
          </Box>
        </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={dashboardPageSx}
    >
      {dashboardHeader}

      <Box sx={DASHBOARD_WORKSPACE_SX}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} role="alert">
          {error}
        </Alert>
      )}

      <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ mb: 1.25, letterSpacing: '0.02em', color: DFT_BRAND.usage }}>
        Current usage {selectedProject?.name ? `· ${selectedProject.name}` : ''}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card
            component="article"
            elevation={0}
            sx={{
              ...metricCardSx,
              height: '100%',
              minHeight: 128,
              cursor: 'pointer',
              transition: 'box-shadow 0.2s, transform 0.15s',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                transform: 'translateY(-2px)',
              },
              '&:focus-within': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
            onClick={() => navigate('/manage-account?tab=team')}
          >
            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ pr: 4 }}>
                    Team Members
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ mt: 1, color: DFT_BRAND.text, fontSize: '1.7rem', letterSpacing: '-0.04em' }}>
                    {totalOrganizationMembers}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                    People in your workspace
                  </Typography>
                </Box>
                <Box sx={metricIconSx} aria-hidden>
                  <PeopleIcon sx={{ fontSize: 16, color: DFT_BRAND.icon }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card
            component="article"
            elevation={0}
            sx={{
              ...metricCardSx,
              height: '100%',
              minHeight: 128,
              cursor: 'pointer',
              transition: 'box-shadow 0.2s, transform 0.15s',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                transform: 'translateY(-2px)',
              },
              '&:focus-within': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
            onClick={() => navigate('/collections')}
          >
            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ pr: 4 }}>
                    Total collections
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ mt: 1, color: DFT_BRAND.text, fontSize: '1.7rem', letterSpacing: '-0.04em' }}>
                    {totalCollections}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                    Collections in this project
                  </Typography>
                </Box>
                <Box sx={metricIconSx} aria-hidden>
                  <FolderIcon sx={{ fontSize: 16, color: DFT_BRAND.icon }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card
            component="article"
            elevation={0}
            sx={{
              ...metricCardSx,
              height: '100%',
              minHeight: 128,
              cursor: 'pointer',
              transition: 'box-shadow 0.2s, transform 0.15s',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                transform: 'translateY(-2px)',
              },
              '&:focus-within': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
            onClick={() => navigate('/collections')}
          >
            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ pr: 4 }}>
                    API calls
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ mt: 1, color: DFT_BRAND.text, fontSize: '1.7rem', letterSpacing: '-0.04em' }}>
                    {totalRequests}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                    API requests defined
                  </Typography>
                </Box>
                <Box sx={metricIconSx} aria-hidden>
                  <ApiIcon sx={{ fontSize: 16, color: DFT_BRAND.icon }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card
            component="article"
            elevation={0}
            sx={{
              ...metricCardSx,
              height: '100%',
              minHeight: 128,
              cursor: 'pointer',
              transition: 'box-shadow 0.2s, transform 0.15s',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                transform: 'translateY(-2px)',
              },
              '&:focus-within': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
            onClick={() => navigate('/test-cycles')}
          >
            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ pr: 4 }}>
                    Test Plan
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ mt: 1, color: DFT_BRAND.text, fontSize: '1.7rem', letterSpacing: '-0.04em' }}>
                    {totalTestCycles}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                    Configured test plans
                  </Typography>
                </Box>
                <Box sx={metricIconSx} aria-hidden>
                  <BarChartIcon sx={{ fontSize: 16, color: DFT_BRAND.icon }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* List cards: Top users, workspaces, collections */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {/* <Grid item xs={12} md={3} sm={6}>
          <Card
            elevation={0}
            sx={{
              ...dashboardCardBorderSx,
              height: '100%',
              minHeight: 250,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent sx={{ p: SPACING.card, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '0.9375rem', lg: '1rem' } }}>
                Top users
              </Typography>
              {user ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      API requests
                    </Typography>
                  </Box>
                  <List dense disablePadding aria-label="Top users list">
                    <ListItem
                      divider
                      sx={{ px: 0 }}
                      secondaryAction={
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      }
                    >
                      <ListItemText
                        primary={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email}
                        secondary={user.email}
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  </List>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No user information available.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid> */}
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card
            elevation={0}
            sx={{
              ...dashboardCardBorderSx,
              height: '100%',
              minHeight: 250,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent sx={{ p: SPACING.card, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '0.9375rem', lg: '1rem' } }}>
                Assertions
              </Typography>
              {selectedProject ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: 0.5,
                  }}
                >
                  <Typography variant="h5" fontWeight={700} sx={{ fontSize: '2rem', letterSpacing: '-0.04em', color: DFT_BRAND.text }}>
                    {totalAssertions ?? ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Total assertions defined in this project.
                  </Typography>
                  <Box
                    sx={{
                      height: 4,
                      bgcolor: DFT_BRAND.tint,
                      borderRadius: '999px',
                      mt: 2,
                      overflow: 'hidden',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        width: '72%',
                        height: 4,
                        borderRadius: '999px',
                        background: 'linear-gradient(90deg, #5f8cef, #5ac0c5)',
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a project to see assertion counts.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card
            elevation={0}
            sx={{
              ...dashboardCardBorderSx,
              height: '100%',
              minHeight: 250,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent sx={{ p: SPACING.card, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '0.9375rem', lg: '1rem' } }}>
                Test Plan
              </Typography>
              {selectedProject ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: 0.5,
                  }}
                >
                  <Typography variant="h5" fontWeight={700} sx={{ fontSize: '2rem', letterSpacing: '-0.04em', color: DFT_BRAND.text }}>
                    {totalTestCycles}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Total test plan configured in this project.
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    sx={dashboardLinkSx}
                    onClick={() => void openTestCyclesDialog()}
                    aria-label="View recent test cycles"
                  >
                    View test plan →
                  </Button>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a project to see test plan.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        {/* <Grid item xs={12} md={3} sm={6}>
          <Card
            elevation={0}
            sx={{
              ...dashboardCardBorderSx,
              height: '100%',
              minHeight: 250,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent sx={{ p: SPACING.card, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '0.9375rem', lg: '1rem' } }}>
                Data sets
              </Typography>
              {selectedProject ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Scope
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Data sets
                    </Typography>
                  </Box>
                  <List dense disablePadding aria-label="Top workspaces list">
                    <ListItem
                      divider
                      sx={{ px: 0 }}
                      secondaryAction={
                        <Typography variant="body2" color="text.secondary">
                          {totalDataSets}
                        </Typography>
                      }
                    >
                      <ListItemText
                        primary={selectedProject.name}
                        secondary="Selected project"
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  </List>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No active workspace selected.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid> */}
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card
            elevation={0}
            sx={{
              ...dashboardCardBorderSx,
              height: '100%',
              minHeight: 250,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent sx={{ p: SPACING.card, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '0.9375rem', lg: '1rem' } }}>
                Recent collections
              </Typography>
              {collections.length > 0 ? (
                <>
                  <List
                    dense
                    disablePadding
                    sx={{
                      flex: 1,
                      maxHeight: 34 * 5,
                      overflowY: 'auto',
                    }}
                    aria-label="Recent collections list"
                  >
                    {collections.map((collection) => (
                      <ListItemButton
                        key={collection.id}
                        onClick={() => navigate(`/collections?collectionId=${collection.id}`)}
                        sx={{
                          px: 1,
                          borderRadius: '10px',
                          mb: 0.5,
                          border: 0,
                          mx: 0,
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                          <ListItemText
                            primary={collection.name}
                            secondary={collection.description || undefined}
                            primaryTypographyProps={{ fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                            sx={{ my: 0.5, minWidth: 0 }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ pr: 1 }}>
                            {collectionRequestCounts[collection.id] ?? 0}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    ))}
                  </List>
                  <Button
                    variant="text"
                    size="medium"
                    onClick={() => setIsCollectionsDialogOpen(true)}
                    sx={dashboardLinkSx}
                    aria-label="View all collections"
                  >
                    View all →
                  </Button>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  No collections found. Create your first collection to get started.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card
            elevation={0}
            sx={{
              ...dashboardCardBorderSx,
              height: '100%',
              minHeight: 250,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent sx={{ p: SPACING.card, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '0.9375rem', lg: '1rem' } }}>
                Recent requests
              </Typography>

              {recentRequests.length > 0 ? (
                <>
                  <List
                    dense
                    disablePadding
                    sx={{
                      flex: 1,
                      maxHeight: 34 * 5,
                      overflowY: 'auto',
                    }}
                    aria-label="Recent requests list"
                  >
                    {recentRequests.map((req) => {
                      const method = (req.method || '').toUpperCase();
                      return (
                        <ListItemButton
                          key={req.id}
                          onClick={() =>
                            navigate(`/collections?collectionId=${req.collectionId}&requestId=${req.id}`)
                          }
                          sx={{
                            px: 1,
                            borderRadius: '10px',
                            mb: 0.5,
                            border: 0,
                            mx: 0,
                          }}
                        >
                          <Box sx={{ width: '100%', pr: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Chip
                                size="small"
                                label={method || 'REQ'}
                                sx={{
                                  bgcolor: DFT_BRAND.methodBg,
                                  color: DFT_BRAND.methodColor,
                                  fontWeight: 700,
                                  height: 20,
                                  borderRadius: '8px',
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  flex: 1,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                title={req.name}
                              >
                                {req.name}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={`${method} ${req.url}`}
                            >
                              {method} {req.url}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {formatShortDate(req.dashboardLastRunAt || req.updatedAt || req.createdAt)}
                            </Typography>
                          </Box>
                        </ListItemButton>
                      );
                    })}
                  </List>
                  <Button
                    variant="text"
                    size="medium"
                    onClick={() => setIsRequestsDialogOpen(true)}
                    sx={dashboardLinkSx}
                    aria-label="View all requests"
                  >
                    View all →
                  </Button>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  No recent requests found. Execute a request to see it appear here.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Test assets over time */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={12}>
          <Card
            elevation={0}
            sx={{
              ...dashboardCardBorderSx,
              height: '100%',
              minHeight: 300
            }}
          >
            <CardContent sx={{ p: SPACING.card }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.25,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '0.9375rem', lg: '1rem' } }}>
                    Test assets over time
                  </Typography>
                  <Tooltip
                    title={
                      <Box sx={{ maxWidth: 340 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: 12 }}>
                          Test assets over time
                        </Typography>
                        <Typography variant="caption" color="text.primary" sx={{ display: 'block', mb: 0.5, fontSize: 10 }}>
                          Shows how your testing artefacts evolve: total requests, collections, datasets,
                          assertions and test cycles. Helpful for understanding coverage growth and maturity.
                        </Typography>
                        <Typography variant="caption" color="text.primary" sx={{ display: 'block', fontSize: 10 }}>
                          Click legend items to focus on individual asset types.
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton size="small" aria-label="About test assets chart">
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Last 6 months
                </Typography>
              </Box>

              {/* Legend */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  mb: 1.25,
                }}
              >
                {TOTAL_ASSETS_SERIES.map((series) => {
                  const isActive = activeAssetSeriesKeys[series.key];
                  return (
                    <Box
                      key={series.key}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        setActiveAssetSeriesKeys((prev) => ({
                          ...prev,
                          [series.key]: !prev[series.key],
                        }))
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setActiveAssetSeriesKeys((prev) => ({
                            ...prev,
                            [series.key]: !prev[series.key],
                          }));
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 10,
                        cursor: 'pointer',
                        bgcolor: isActive ? 'action.selected' : 'transparent',
                        opacity: isActive ? 1 : 0.4,
                        outline: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: series.color,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {series.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Line chart with shared tooltip */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'stretch',
                }}
              >
                {/* Y-axis scale */}
                <Box
                  sx={{
                    width: 40,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    py: 1,
                  }}
                >
                  {getAxisTicks(
                    Math.max(
                      ...totalAssetsData.map((bucket) =>
                        (Object.keys(bucket.values) as AssetSeriesKey[]).reduce(
                          (sum, key) =>
                            activeAssetSeriesKeys[key]
                              ? sum + bucket.values[key]
                              : sum,
                          0,
                        ),
                      ),
                    ),
                    3,
                  ).map((tick) => (
                    <Typography
                      key={tick}
                      variant="caption"
                      color="text.secondary"
                      sx={{ textAlign: 'right' }}
                    >
                      {tick}
                    </Typography>
                  ))}
                </Box>

                {/* SVG multi-series line chart */}
                <Box sx={{ flex: 1, minWidth: 0, height: CHART_BOX_HEIGHT }}>
                  <svg
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    role="img"
                    aria-label="Line chart showing total test assets over time"
                    style={{ width: '100%', height: 'calc(100% - 26px)' }}
                  >
                    {/* Grid lines */}
                    {[0, 33.33, 66.66, 100].map((y) => (
                      <line
                        key={y}
                        x1="0"
                        y1={y}
                        x2="100"
                        y2={y}
                        stroke="#e5e7eb"
                        strokeWidth="0.3"
                      />
                    ))}
                    {TOTAL_ASSETS_SERIES.map((series) => {
                      if (!activeAssetSeriesKeys[series.key]) {
                        return null;
                      }
                      const maxValue =
                        Math.max(
                          ...totalAssetsData.map((bucket) =>
                            (Object.keys(bucket.values) as AssetSeriesKey[]).reduce(
                              (sum, key) =>
                                activeAssetSeriesKeys[key]
                                  ? sum + bucket.values[key]
                                  : sum,
                              0,
                            ),
                          ),
                        ) || 1;
                      const points = totalAssetsData.map((bucket, index) => {
                        const x =
                          (index / (totalAssetsData.length - 1 || 1)) * 100;
                        const y =
                          100 -
                          ((bucket.values[series.key] || 0) / maxValue) * 100;
                        return `${x},${y}`;
                      }).join(' ');
                      return (
                        <polyline
                          key={series.key}
                          points={points}
                          fill="none"
                          stroke={series.color}
                          strokeWidth={1.5}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      );
                    })}

                    {/* Hover regions for shared tooltip */}
                    {totalAssetsData.map((bucket, index) => {
                      const sliceWidth =
                        100 / (totalAssetsData.length || 1);
                      const x = index * sliceWidth;
                      return (
                        <rect
                          key={bucket.label}
                          x={x}
                          y={0}
                          width={sliceWidth}
                          height={100}
                          fill="transparent"
                          onMouseEnter={(event) =>
                            setAssetSeriesTooltip({
                              label: bucket.label,
                              values: bucket.values,
                              x: event.clientX + 12,
                              y: event.clientY + 12,
                            })
                          }
                          onMouseMove={(event) =>
                            setAssetSeriesTooltip((current) =>
                              current
                                ? {
                                    ...current,
                                    x: event.clientX + 12,
                                    y: event.clientY + 12,
                                  }
                                : current,
                            )
                          }
                          onMouseLeave={() => setAssetSeriesTooltip(null)}
                        />
                      );
                    })}
                  </svg>

                  {/* X-axis labels */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 0.5,
                      px: 0.5,
                      minHeight: 20,
                    }}
                  >
                    {totalAssetsData.map((bucket) => (
                      <Typography
                        key={bucket.label}
                        variant="caption"
                        color="text.secondary"
                      >
                        {bucket.label}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Shared tooltip */}
              {assetSeriesTooltip && (
                <Box
                  sx={{
                    position: 'fixed',
                    left: assetSeriesTooltip.x,
                    top: assetSeriesTooltip.y,
                    transform: 'translateY(-2px)',
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    boxShadow: 4,
                    px: 1.5,
                    py: 1,
                    zIndex: 1300,
                    minWidth: 200,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}
                  >
                    {assetSeriesTooltip.label}
                  </Typography>
                  {TOTAL_ASSETS_SERIES.map((series) => (
                    <Box
                      key={series.key}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mt: 0.25,
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: series.color,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {series.label}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.primary">
                        {assetSeriesTooltip.values[series.key] ?? 0}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Test cycles – requests, data sets & assertions */}
      <Grid container spacing={2} sx={{ mb: 2.5}}>
        <Grid item xs={12} md={12}>
          <Card
            elevation={0}
            sx={{
              ...dashboardCardBorderSx,
              height: '100%',
            }}
          >
            <CardContent sx={{ p: SPACING.card }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.25,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '0.9375rem', lg: '1rem' }, }}>
                    Test Plan by assets
                  </Typography>
                  <Tooltip
                    title={
                      <Box sx={{ maxWidth: 340 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: 12 }}>
                          Test Plan – requests, data sets and assertions
                        </Typography>
                        <Typography variant="caption" color="text.primary" sx={{ display: 'block', mb: 0.5, fontSize: 10 }}>
                          Shows how many requests, data sets and assertions are associated with your
                          test cycles over time, helping you understand test depth and complexity.
                        </Typography>
                        <Typography variant="caption" color="text.primary" sx={{ display: 'block', fontSize: 10 }}>
                          Use the legend buttons to focus on individual asset types.
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton size="small" aria-label="About test cycle asset chart">
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl sx={{ minWidth: 180, '& .MuiSelect-select': { minHeight: '20px !important', py: '6px', }, }}>
                  <InputLabel id="test-cycle-select-label">Test plan</InputLabel>
                  <Select
                    labelId="test-cycle-select-label"
                    id="test-cycle-select"
                    label="Test cycle"
                    size="small"
                    value={selectedTestCycleId === 'all' ? 'all' : String(selectedTestCycleId)}
                   
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value === 'all') {
                        setSelectedTestCycleId('all');
                      } else {
                        const idNum = Number(value);
                        setSelectedTestCycleId(Number.isNaN(idNum) ? 'all' : idNum);
                      }
                    }}
                  >
                    <MenuItem sx={{fontSize: 12}} value="all">All test plan</MenuItem>
                    {testCycles.map((cycle) => (
                      <MenuItem sx={{fontSize: 12}} key={cycle.id} value={String(cycle.id)}>
                        {cycle.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <ToggleButtonGroup
                  value={testCycleAssetViewMode}
                  exclusive
                  onChange={(_, v) => v != null && setTestCycleAssetViewMode(v)}
                  size="small"
                  sx={{
                    backgroundColor: DFT_BRAND.bg,
                    p: 0.4,
                    borderRadius: '12px',
                    '& .MuiToggleButtonGroup-grouped': { border: 0 },
                    '& .MuiToggleButton-root': {
                      py: 0.5,
                      px: 1,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      border: 0,
                      borderRadius: '10px',
                      color: DFT_BRAND.muted,
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                      backgroundColor: DFT_BRAND.paper,
                      color: DFT_BRAND.text,
                      boxShadow: '0 1px 4px rgba(24, 35, 58, 0.08)',
                    },
                  }}
                >
                  <ToggleButton value="total">Total number</ToggleButton>
                  <ToggleButton value="percentage">Percentage</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

<Box sx={{ width: '100%', height: CHART_BOX_HEIGHT }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={
                      testCycleAssetViewMode === 'percentage'
                        ? testCycleAssetData.map((row) => {
                            const total = row.requests + row.datasets + row.assertions;
                            if (total === 0) return { month: row.month, requests: 0, datasets: 0, assertions: 0 };
                            return {
                              month: row.month,
                              requests: Math.round((row.requests / total) * 1000) / 10,
                              datasets: Math.round((row.datasets / total) * 1000) / 10,
                              assertions: Math.round((row.assertions / total) * 1000) / 10,
                            };
                          })
                        : testCycleAssetData
                    }
                    margin={{ top: 12, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      tickLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      tickLine={{ stroke: theme.palette.divider }}
                      domain={testCycleAssetViewMode === 'percentage' ? [0, 100] : undefined}
                      tickFormatter={testCycleAssetViewMode === 'percentage' ? (v) => `${v}%` : undefined}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '12px',
                      }}
                      labelStyle={{ color: theme.palette.text.primary }}
                      itemStyle={{ color: theme.palette.text.secondary }}
                      formatter={(value?: number, name?: string) => {
                        const safeValue = value ?? 0;
                        const label =
                          TEST_CYCLE_ASSET_SERIES.find((s) => s.key === name) ||
                          TEST_CYCLE_ASSET_SERIES.find((s) => s.label === name);
                        return [
                          testCycleAssetViewMode === 'percentage' ? `${safeValue}%` : safeValue,
                          label?.label ?? name ?? '',
                        ];
                      }}
                    />
                    <Legend formatter={(v) => <span style={{ color: theme.palette.text.secondary, fontSize: 12 }}>{v}</span>} />
                    <Bar dataKey="requests" stackId="a" fill={DFT_BRAND.chartBlue} name="Requests" />
                    <Bar dataKey="datasets" stackId="a" fill={DFT_BRAND.chartOrange} name="Data sets" />
                    <Bar dataKey="assertions" stackId="a" fill={DFT_BRAND.chartGold} name="Assertions" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Engagement & API response analytics */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {/* Team member engagement over time (multi-series line chart) */}
        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              ...dashboardCardBorderSx,
              height: '100%',
            }}
          >
            <CardContent sx={{ p: SPACING.card }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.25,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '0.9375rem', lg: '1rem' } }}>
                    Team member engagement over time
                  </Typography>
                  <Tooltip
                    title={
                      <Box sx={{ maxWidth: 360 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: 12 }}>
                          Team member engagement over time
                        </Typography>
                        <Typography variant="caption" color="text.primary" sx={{ display: 'block', mb: 1, fontSize: 10 }}>
                          Tracks user progression through the API adoption funnel: active users, API users,
                          new API users, successful API users, and error‑only API users. Helps you identify
                          onboarding gaps and where users may need support.
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25, fontSize: 10 }}>
                          Metrics
                        </Typography>
                        <Typography variant="caption" color="text.primary" sx={{ display: 'block', fontSize: 10 }}>
                          <strong>Active users</strong>: Per calendar month, 1 while you are signed in and this project
                          had at least one API execution in that month; 0 otherwise. Same six-month window as the chart
                          (not an organization-wide distinct-user count).
                          <br />
                          <strong>API users</strong>: Users who made at least one API call.
                          <br />
                          <strong>New API users</strong>: Users making API calls for the first time.
                          <br />
                          <strong>Successful API users</strong>: Users whose calls are mostly successful.
                          <br />
                          <strong>Error‑only API users</strong>: Users whose calls only return errors.
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton size="small" aria-label="About team engagement chart">
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Last 6 months
                </Typography>
              </Box>

              {/* Legend */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  mb: 1.25,
                }}
              >
                {teamEngagementSeries.map((series) => {
                  const isActive = activeEngagementSeriesKeys[series.key];
                  return (
                    <Box
                      key={series.key}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        setActiveEngagementSeriesKeys((prev) => ({
                          ...prev,
                          [series.key]: !prev[series.key],
                        }))
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setActiveEngagementSeriesKeys((prev) => ({
                            ...prev,
                            [series.key]: !prev[series.key],
                          }));
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 10,
                        cursor: 'pointer',
                        bgcolor: isActive ? 'action.selected' : 'transparent',
                        opacity: isActive ? 1 : 0.4,
                        outline: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: series.color,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {series.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Chart with numeric Y scale */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'stretch',
                }}
              >
                {/* Y-axis scale */}
                <Box
                  sx={{
                    width: 32,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    py: 1,
                  }}
                >
                  {[4.5, 3, 1.5, 0].map((tick) => (
                    <Typography
                      key={tick}
                      variant="caption"
                      color="text.secondary"
                      sx={{ textAlign: 'right' }}
                    >
                      {tick}
                    </Typography>
                  ))}
                </Box>

                {/* SVG line chart */}
                <Box sx={{ flex: 1, minWidth: 0, height: CHART_BOX_HEIGHT }}>
                  <svg
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    role="img"
                    aria-label="Line chart showing team member engagement over the last six months"
                    style={{ width: '100%', height: 'calc(100% - 26px)' }}
                  >
                    {/* Grid lines */}
                    {[0, 33.33, 66.66, 100].map((y) => (
                      <line
                        key={y}
                        x1="0"
                        y1={y}
                        x2="100"
                        y2={y}
                        stroke="#e5e7eb"
                        strokeWidth="0.3"
                      />
                    ))}
                    {teamEngagementSeries.map((series) => {
                      if (!activeEngagementSeriesKeys[series.key]) {
                        return null;
                      }
                      const maxValue = 4.5;
                      const points = series.values
                        .map((value, index) => {
                          const x =
                            (index / (teamEngagementMonths.length - 1 || 1)) * 100;
                          const y = 100 - (value / maxValue) * 100;
                          return `${x},${y}`;
                        })
                        .join(' ');
                      return (
                        <polyline
                          key={series.key}
                          points={points}
                          fill="none"
                          stroke={series.color}
                          strokeWidth={1.5}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      );
                    })}
                  </svg>
                  {/* X-axis labels */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 0.5,
                      px: 0.5,
                      minHeight: 20,
                    }}
                  >
                    {teamEngagementMonths.map((month) => (
                      <Typography
                        key={month}
                        variant="caption"
                        color="text.secondary"
                      >
                        {month}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* API requests by response code (Recharts stacked bar chart) */}
        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              ...dashboardCardBorderSx,
              height: '100%',
            }}
          >
            <CardContent sx={{ p: SPACING.card }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.25,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '0.9375rem', lg: '1rem' } }}>
                    API requests by response code
                  </Typography>
                  <Tooltip
                    title={
                      <Box sx={{ maxWidth: 320 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5,  fontSize: 12 }}>
                          API requests by response code
                        </Typography>
                        <Typography variant="caption" color="text.primary" sx={{ display: 'block', mb: 0.5,  fontSize: 10 }}>
                          Shows how many API requests returned 2XX, 3XX, 4XX and 5XX responses each month.
                          Use the toggle to switch between total counts and percentage.
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton size="small" aria-label="About API requests chart">
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <ToggleButtonGroup
                  value={apiResponseViewMode}
                  exclusive
                  onChange={(_, v) => v != null && setApiResponseViewMode(v)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      py: 0.25,
                      px: 1.25,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                    },
                  }}
                >
                  <ToggleButton value="total">Total number</ToggleButton>
                  <ToggleButton value="percentage">Percentage</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ width: '100%', height: CHART_BOX_HEIGHT }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={
                      apiResponseViewMode === 'percentage'
                        ? apiResponseData.map((row) => {
                            const total =
                              row['2XX'] + row['3XX'] + row['4XX'] + row['5XX'];
                            if (total === 0) {
                              return {
                                month: row.month,
                                '2XX': 0,
                                '3XX': 0,
                                '4XX': 0,
                                '5XX': 0,
                              };
                            }
                            return {
                              month: row.month,
                              '2XX': Math.round((row['2XX'] / total) * 1000) / 10,
                              '3XX': Math.round((row['3XX'] / total) * 1000) / 10,
                              '4XX': Math.round((row['4XX'] / total) * 1000) / 10,
                              '5XX': Math.round((row['5XX'] / total) * 1000) / 10,
                            };
                          })
                        : apiResponseData
                    }
                    margin={{ top: 12, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      tickLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      tickLine={{ stroke: theme.palette.divider }}
                      domain={apiResponseViewMode === 'percentage' ? [0, 100] : undefined}
                      tickFormatter={apiResponseViewMode === 'percentage' ? (v) => `${v}%` : undefined}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '12px',
                      }}
                      labelStyle={{ color: theme.palette.text.primary }}
                      itemStyle={{ color: theme.palette.text.secondary }}
                      formatter={(value?: number, name?: string) => {
                        const safeValue = value ?? 0;
                        return [
                          apiResponseViewMode === 'percentage' ? `${safeValue}%` : safeValue,
                          name ?? '',
                        ];
                      }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: 8 }}
                      formatter={(value) => (
                        <span style={{ color: theme.palette.text.secondary, fontSize: 12 }}>
                          {value}
                        </span>
                      )}
                    />
                    {API_RESPONSE_KEYS.map((key) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        stackId="a"
                        fill={API_RESPONSE_COLORS[key]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User distribution over time (Recharts) */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={12}>
          <Card elevation={0} sx={dashboardCardBorderSx}>
            <CardContent sx={{ p: SPACING.card }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '0.9375rem', lg: '1rem' } }}>
                    User distribution over time
                  </Typography>
                  <Tooltip title={
                    <Box sx={{ maxWidth: 320 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5,  fontSize: 12 }}>User distribution over time</Typography>
                      <Typography variant="caption" color="text.primary" sx={{ display: 'block', mb: 0.5, fontSize: 10 }}>
                        Each month is 1 while you are signed in and this project had at least one API execution in that
                        month, otherwise 0—not a multi-user headcount.
                      </Typography>
                    </Box>
                  }>
                    <IconButton size="small" aria-label="About user distribution chart">
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <ToggleButtonGroup
                  value={userDistributionViewMode}
                  exclusive
                  onChange={(_, v) => v != null && setUserDistributionViewMode(v)}
                  size="small"
                  sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, textTransform: 'none', fontSize: '0.75rem', borderRadius: '10px' } }}
                >
                  <ToggleButton value="total">Total number</ToggleButton>
                  <ToggleButton value="percentage">Percentage</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ width: '100%', height: CHART_BOX_HEIGHT }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={
                      userDistributionViewMode === 'percentage'
                        ? userDistributionChartData.map((row) => {
                            const total = userDistributionChartData.reduce((s, d) => s + d['Active users'], 0);
                            const pct = total > 0 ? Math.round((row['Active users'] / total) * 1000) / 10 : 0;
                            return { month: row.month, 'Active users': pct };
                          })
                        : userDistributionChartData
                    }
                    margin={{ top: 12, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      tickLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      tickLine={{ stroke: theme.palette.divider }}
                      domain={userDistributionViewMode === 'percentage' ? [0, 100] : undefined}
                      tickFormatter={userDistributionViewMode === 'percentage' ? (v) => `${v}%` : undefined}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '12px',
                      }}
                      labelStyle={{ color: theme.palette.text.primary }}
                      formatter={(value?: number) => {
                        const safeValue = value ?? 0;
                        return [
                          userDistributionViewMode === 'percentage' ? `${safeValue}%` : safeValue,
                          'Active users',
                        ];
                      }}
                    />
                    <Legend formatter={() => <span style={{ color: theme.palette.text.secondary, fontSize: 12 }}>Active users</span>} />
                    <Bar dataKey="Active users" fill="#2563eb" name="Active users" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Workspaces analytics */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {/* Active workspaces over time */}
        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              ...dashboardCardBorderSx,
              height: '100%',
            }}
          >
            <CardContent sx={{ p: SPACING.card }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.25,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '0.9375rem', lg: '1rem' } }}>
                    Active workspaces over time
                  </Typography>
                  <Tooltip
                    title={
                      <Box sx={{ maxWidth: 320 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: 12 }}>
                          Active workspaces over time
                        </Typography>
                        <Typography variant="caption" color="text.primary" sx={{ display: 'block', mb: 0.5, fontSize: 10 }}>
                          Shows how many internal, partner, private and public workspaces are active each month,
                          so you can see which workspace types are being used most.
                        </Typography>
                        <Typography variant="caption" color="text.primary" sx={{ display: 'block', fontSize: 10 }}>
                          Click legend items to include or exclude workspace types.
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton size="small" aria-label="About active workspaces chart">
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <ToggleButtonGroup
                  value={activeWorkspacesViewMode}
                  exclusive
                  onChange={(_, v) => v != null && setActiveWorkspacesViewMode(v)}
                  size="small"
                  sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, textTransform: 'none', fontSize: '0.75rem', borderRadius: '10px' } }}
                >
                  <ToggleButton value="total">Total number</ToggleButton>
                  <ToggleButton value="percentage">Percentage</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Recharts stacked bar chart */}
              <Box sx={{ width: '100%', height: CHART_BOX_HEIGHT }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={
                      activeWorkspacesViewMode === 'percentage'
                        ? activeWorkspacesChartData.map((row) => {
                            const total = row.Internal + row.Partner + row.Private + row.Public;
                            if (total === 0) return { month: row.month, Internal: 0, Partner: 0, Private: 0, Public: 0 };
                            return {
                              month: row.month,
                              Internal: Math.round((row.Internal / total) * 1000) / 10,
                              Partner: Math.round((row.Partner / total) * 1000) / 10,
                              Private: Math.round((row.Private / total) * 1000) / 10,
                              Public: Math.round((row.Public / total) * 1000) / 10,
                            };
                          })
                        : activeWorkspacesChartData
                    }
                    margin={{ top: 12, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      tickLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      tickLine={{ stroke: theme.palette.divider }}
                      domain={activeWorkspacesViewMode === 'percentage' ? [0, 100] : undefined}
                      tickFormatter={activeWorkspacesViewMode === 'percentage' ? (v) => `${v}%` : undefined}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '12px',
                      }}
                      labelStyle={{ color: theme.palette.text.primary }}
                      itemStyle={{ color: theme.palette.text.secondary }}
                      formatter={(value?: number) => {
                        const safeValue = value ?? 0;
                        return [
                          activeWorkspacesViewMode === 'percentage' ? `${safeValue}%` : safeValue,
                        ];
                      }}
                    />
                    <Legend formatter={(v) => <span style={{ color: theme.palette.text.secondary, fontSize: 12 }}>{v}</span>} />
                    <Bar dataKey="Internal" stackId="a" fill="#38bdf8" name="Internal workspaces" />
                    <Bar dataKey="Partner" stackId="a" fill="#f97316" name="Partner workspaces" />
                    <Bar dataKey="Private" stackId="a" fill="#eab308" name="Private workspaces" />
                    <Bar dataKey="Public" stackId="a" fill="#8b5cf6" name="Public workspaces" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Elements in workspaces over time */}
        <Grid item xs={12} lg={6}>
          <Card
            elevation={0}
            sx={{
              ...dashboardCardBorderSx,
              height: '100%',
            }}
          >
            <CardContent sx={{ p: SPACING.card }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.25,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '0.9375rem', lg: '1rem' } }}>
                    Elements in workspaces over time
                  </Typography>
                  <Tooltip
                    title={
                      <Box sx={{ maxWidth: 320 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: 12 }}>
                          Elements in workspaces over time
                        </Typography>
                        <Typography variant="caption" color="text.primary" sx={{ display: 'block', mb: 0.5, fontSize: 10 }}>
                          Tracks how collections, environments, mocks and monitors accumulate as you build out
                          API tests, showing growth in different asset types.
                        </Typography>
                        <Typography variant="caption" color="text.primary" sx={{ display: 'block', fontSize: 10 }}>
                          Click legend items to focus on specific element types.
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton size="small" aria-label="About elements in workspaces chart">
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <ToggleButtonGroup
                  value={workspaceElementsViewMode}
                  exclusive
                  onChange={(_, v) => v != null && setWorkspaceElementsViewMode(v)}
                  size="small"
                  sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, textTransform: 'none', fontSize: '0.75rem', borderRadius: '10px' } }}
                >
                  <ToggleButton value="total">Total number</ToggleButton>
                  <ToggleButton value="percentage">Percentage</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Recharts stacked bars */}
              <Box sx={{ width: '100%', height: CHART_BOX_HEIGHT }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={
                      workspaceElementsViewMode === 'percentage'
                        ? workspaceElementsChartData.map((row) => {
                            const total = row.Collections + row.Environments + row.Mocks + row.Monitors;
                            if (total === 0) return { month: row.month, Collections: 0, Environments: 0, Mocks: 0, Monitors: 0 };
                            return {
                              month: row.month,
                              Collections: Math.round((row.Collections / total) * 1000) / 10,
                              Environments: Math.round((row.Environments / total) * 1000) / 10,
                              Mocks: Math.round((row.Mocks / total) * 1000) / 10,
                              Monitors: Math.round((row.Monitors / total) * 1000) / 10,
                            };
                          })
                        : workspaceElementsChartData
                    }
                    margin={{ top: 12, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      tickLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      tickLine={{ stroke: theme.palette.divider }}
                      domain={workspaceElementsViewMode === 'percentage' ? [0, 100] : undefined}
                      tickFormatter={workspaceElementsViewMode === 'percentage' ? (v) => `${v}%` : undefined}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '12px',
                      }}
                      labelStyle={{ color: theme.palette.text.primary }}
                      itemStyle={{ color: theme.palette.text.secondary }}
                      formatter={(value?: number) => {
                        const safeValue = value ?? 0;
                        return [
                          workspaceElementsViewMode === 'percentage' ? `${safeValue}%` : safeValue,
                        ];
                      }}
                    />
                    <Legend formatter={(v) => <span style={{ color: theme.palette.text.secondary, fontSize: 12 }}>{v}</span>} />
                    <Bar dataKey="Collections" stackId="a" fill="#2563eb" name="Collections" />
                    <Bar dataKey="Environments" stackId="a" fill="#f97316" name="Environments" />
                    <Bar dataKey="Mocks" stackId="a" fill="#eab308" name="Mocks" />
                    <Bar dataKey="Monitors" stackId="a" fill="#22c55e" name="Monitors" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Box>
      <Dialog
        open={isCollectionsDialogOpen}
        onClose={() => setIsCollectionsDialogOpen(false)}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 6 }}
        PaperProps={{
          sx: {
            width: 'clamp(500px, 60vw, 800px)',
            maxWidth: '90vw',
            mt: '64px',
            maxHeight: 'calc(100vh - 96px)',
            borderRadius: DASHBOARD_CARD_RADIUS,
          },
        }}
        maxWidth="md"
      >
        <DialogTitle sx={{ pt: 2.5, pb: 1.5 }}>Last 25 collections</DialogTitle>
        <DialogContent dividers>
          <List dense disablePadding>
            {latest25Collections.map((collection) => (
              <ListItemButton
                key={collection.id}
                onClick={() => {
                  setIsCollectionsDialogOpen(false);
                  navigate(`/collections?collectionId=${collection.id}`);
                }}
                sx={{ borderRadius: '12px', mb: 0.5, border: '1px solid', borderColor: 'divider' }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                  <ListItemText
                    primary={collection.name}
                    secondary={collection.description || `Updated ${formatShortDate(collection.updatedAt || collection.createdAt)}`}
                    primaryTypographyProps={{ fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {collectionRequestCounts[collection.id] ?? 0}
                  </Typography>
                </Box>
              </ListItemButton>
            ))}
            {latest25Collections.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No collections found.
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCollectionsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRequestsDialogOpen}
        onClose={() => setIsRequestsDialogOpen(false)}
        maxWidth="md"
        sx={{ zIndex: (theme) => theme.zIndex.modal + 6 }}
        PaperProps={{
          sx: {
            width: 'clamp(500px, 60vw, 800px)',
            maxWidth: '90vw',
            mt: '64px',
            maxHeight: 'calc(100vh - 96px)',
            borderRadius: DASHBOARD_CARD_RADIUS,
          },
        }}
      >
        <DialogTitle>Last 25 requests</DialogTitle>
        <DialogContent dividers>
          <List dense disablePadding>
            {latest25Requests.map((req) => {
              const method = (req.method || '').toUpperCase();
              return (
                <ListItemButton
                  key={req.id}
                  onClick={() => {
                    setIsRequestsDialogOpen(false);
                    navigate(`/collections?collectionId=${req.collectionId}&requestId=${req.id}`);
                  }}
                  sx={{ borderRadius: '12px', mb: 0.5, border: '1px solid', borderColor: 'divider' }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        label={method || 'REQ'}
                        sx={{
                          bgcolor: getMethodColor(method),
                          color: 'common.white',
                          fontWeight: 700,
                          height: 22,
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {req.name}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {method} {req.url}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {formatShortDate(req.dashboardLastRunAt || req.updatedAt || req.createdAt)}
                    </Typography>
                  </Box>
                </ListItemButton>
              );
            })}
            {latest25Requests.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No requests found.
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRequestsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isTestCyclesDialogOpen}
        onClose={() => setIsTestCyclesDialogOpen(false)}
        maxWidth="md"
        PaperProps={{
          sx: {
            width: 'clamp(500px, 60vw, 800px)',
            maxWidth: '90vw',
            borderRadius: DASHBOARD_CARD_RADIUS,
          },
        }}
      >
        <DialogTitle>Last 30 test plan</DialogTitle>
        <DialogContent dividers>
          <List dense disablePadding sx={{ maxHeight: 480, overflowY: 'auto' }}>
            {latest30TestCycles.map((cycle: { id: number; name?: string; description?: string; updatedAt?: string; createdAt?: string }) => (
              <ListItemButton
                key={cycle.id}
                onClick={() => {
                  setIsTestCyclesDialogOpen(false);
                  navigate(`/test-cycles/${cycle.id}`);
                }}
                sx={{ borderRadius: '12px', mb: 0.5, border: '1px solid', borderColor: 'divider' }}
              >
                <ListItemText
                  primary={cycle.name || `Test cycle #${cycle.id}`}
                  secondary={
                    cycle.description ||
                    `Updated ${formatShortDate(cycle.updatedAt || cycle.createdAt)}`
                  }
                  primaryTypographyProps={{ fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                />
              </ListItemButton>
            ))}
            {latest30TestCycles.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No test plan found.
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTestCyclesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
